<template>
  <div ref="container" tabindex="0" @keydown="onKeydown" class="outline-none">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
      <div class="flex items-start justify-between">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">{{ headerTitle }}</h2>
          <p class="text-sm text-gray-500 mt-1 max-w-xl">
            Track the status of your requests, report issues, or submit new ideas.
            Our team reviews every submission and will follow up with you directly.
          </p>
        </div>
        <div class="flex items-center gap-2 shrink-0 ml-6 mt-0.5">
          <!-- Screen-share safety (item 7): staff can preview exactly what a
               customer sees. Customers never see this toggle. -->
          <button
            v-if="isStaffUser"
            type="button"
            class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border transition-colors"
            :class="customerPreview
              ? 'border-primary-300 bg-primary-50 text-primary-700'
              : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'"
            :title="customerPreview
              ? 'You are seeing the customer view. Click to return to the staff view.'
              : 'Preview exactly what customers see (hides internal rows and staff-only controls).'"
            :aria-pressed="customerPreview"
            aria-label="Toggle customer view preview"
            @click="customerPreview = !customerPreview"
          >
            <FontAwesomeIcon :icon="['fas', customerPreview ? 'eye' : 'eye-slash']" class="text-[10px]" />
            {{ customerPreview ? 'Customer view: On' : 'Customer view' }}
          </button>
          <button
            class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
            @click="openCreate('Bug')"
          >
            <FontAwesomeIcon :icon="['fas', 'bug']" class="text-[10px]" />
            Report a Bug
          </button>
          <button
            class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
            @click="openCreate('Support Ticket')"
          >
            <FontAwesomeIcon :icon="['fas', 'ticket']" class="text-[10px]" />
            Submit a Ticket
          </button>
          <button
            class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            @click="openCreate('Feature')"
          >
            <FontAwesomeIcon :icon="['fas', 'lightbulb']" class="text-[10px]" />
            Request a Feature
          </button>
        </div>
      </div>
    </div>

    <!-- Customer-preview banner (screen-share safety, item 7) -->
    <div
      v-if="isStaffUser && customerPreview"
      class="flex items-center gap-2 px-4 py-2 text-xs bg-primary-600 text-white"
    >
      <FontAwesomeIcon :icon="['fas', 'eye']" />
      <span>You're previewing what customers see — internal rows, notes, and staff controls are hidden.</span>
      <button
        class="ml-auto font-semibold underline underline-offset-2 hover:text-white/80"
        @click="customerPreview = false"
      >
        Exit preview
      </button>
    </div>

    <!-- Stale data banner -->
    <div
      v-if="fetchError"
      class="flex items-center gap-2 px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200"
    >
      <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" />
      <span>Couldn't refresh — showing the last data we had.</span>
      <button class="font-semibold text-amber-900 hover:underline ml-1" @click="retryNow">Retry</button>
    </div>

    <!-- Toolbar -->
    <div class="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
      <div class="grow max-w-sm">
        <div class="relative">
          <i
            class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs"
            :class="isIdSearch ? 'text-primary-400' : 'text-gray-400'"
            title="Search by title, description, requester, or ticket #ID. Typing a bare number (e.g. 42) finds that ticket exactly."
          />
          <input
            v-model="search"
            type="text"
            placeholder="Search by title, #ID, description..."
            class="w-full pl-9 pr-8 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            :class="isIdSearch
              ? 'border-primary-300 bg-primary-50/40'
              : 'border-gray-300'"
          />
          <button
            v-if="search"
            class="absolute right-2.5 inset-y-0 flex items-center text-gray-400 hover:text-gray-600"
            @click="search = ''"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xs" />
          </button>
        </div>
        <!-- Exact-ID mode hint — shown only when the query is purely numeric -->
        <p
          v-if="isIdSearch"
          class="text-[11px] text-primary-600 mt-1 pl-1 flex items-center gap-1"
        >
          <FontAwesomeIcon :icon="['fas', 'hashtag']" class="text-[9px]" />
          Looking up ticket #{{ search.replace(/^#/, '') }} exactly — add a letter to search text instead
        </p>
      </div>
      <div class="flex items-center gap-1">
        <!-- Group 1: Type filter chips -->
        <button
          v-for="chip in collapsedTypeChips"
          :key="chip.key"
          type="button"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="isTypeFilterActive(chip) ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="toggleType(chip)"
        >
          {{ chip.label }}
        </button>

        <span class="w-px h-4 bg-gray-200 mx-0.5 shrink-0"></span>

        <!-- Group 2: Preset chips -->
        <button
          type="button"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="!activePreset && !hasActiveColumnFilters ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          @click="setPreset(null)"
        >
          All
        </button>
        <button
          v-if="current_user"
          type="button"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="activePreset === 'my_open' ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          title="Your tickets that are still open"
          @click="setPreset('my_open')"
        >
          My Open
        </button>
        <button
          type="button"
          class="px-2.5 py-1 text-xs rounded transition-colors"
          :class="activePreset === 'needs_input' ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          :title="isCustomerView ? 'Tickets waiting on your reply' : 'Tickets blocked on the customer'"
          @click="setPreset('needs_input')"
        >
          Needs Input
        </button>

        <!-- Filters button -->
        <button
          ref="filterPanelBtn"
          type="button"
          class="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded border transition-colors ml-1"
          :class="activeFilterCount > 0
            ? 'bg-primary-500 text-white border-primary-500'
            : 'border-gray-300 text-gray-600 hover:bg-gray-50'"
          @click.stop="openFilterPanel"
        >
          <FontAwesomeIcon :icon="['fas', 'filter']" class="text-[10px]" />
          Filters
          <span
            v-if="activeFilterCount > 0"
            class="bg-white/30 rounded-full px-1.5 text-[10px] font-semibold leading-tight"
          >{{ activeFilterCount }}</span>
        </button>
      </div>
      <!-- Answers "am I seeing everything?", which grouping, collapsing and
           pagination otherwise make unanswerable. Phrased as a hidden count
           rather than "N of M" on purpose: the pagination footer already reads
           "1-50 of <matches>", and two different "of N" totals on one screen is
           worse than no count at all. -->
      <div v-if="isNarrowed || sortField" class="flex items-center gap-2 ml-auto">
        <span v-if="hiddenCount > 0" class="text-xs text-gray-500">
          <span class="font-medium text-gray-700">{{ hiddenCount }}</span>
          hidden by filters
        </span>
        <button
          type="button"
          class="text-xs text-gray-600 hover:text-gray-800 inline-flex items-center gap-1 px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 transition-colors"
          @click="resetAll"
        >
          <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-[10px]" />
          {{ resetLabel }}
        </button>
      </div>
      <span
        v-if="refreshing"
        class="text-xs text-gray-400 flex items-center gap-1.5"
        :class="{ 'ml-auto': !sortField && !isNarrowed }"
      >
        <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
        Updating...
      </span>
      <div
        v-else-if="updatedLabel"
        class="flex items-center gap-2 text-xs text-gray-400"
        :class="{ 'ml-auto': !sortField && !hasActiveColumnFilters }"
      >
        <span>{{ updatedLabel }}</span>
        <button class="hover:text-gray-600" title="Refresh" @click="retryNow">
          <FontAwesomeIcon :icon="['fas', 'rotate-right']" />
        </button>
      </div>
      <div class="flex items-center gap-1" :class="{ 'ml-auto': !refreshing && !updatedLabel && !sortField && !hasActiveColumnFilters }">
        <div class="flex rounded border border-gray-300 overflow-hidden mr-1">
          <button
            class="text-xs px-2 py-1 transition-colors"
            :class="viewMode === 'table' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600'"
            @click="viewMode = 'table'"
          >
            <FontAwesomeIcon :icon="['fas', 'list']" />
          </button>
          <button
            class="text-xs px-2 py-1 border-l border-gray-300 transition-colors"
            :class="viewMode === 'board' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600'"
            @click="viewMode = 'board'"
          >
            <FontAwesomeIcon :icon="['fas', 'table']" />
          </button>
        </div>
        <button
          v-if="viewMode === 'table'"
          class="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 border border-gray-300 rounded transition-colors"
          @click="toggleExpandAll"
        >
          {{ allExpanded ? 'Collapse' : 'Expand' }}
        </button>
        <button
          v-if="effectiveStaffView"
          class="text-xs px-2 py-1 border rounded transition-colors"
          :class="bulkMode
            ? 'text-primary-600 border-primary-300 bg-primary-50'
            : 'text-gray-400 border-gray-300 hover:text-gray-600'"
          @click="toggleBulkMode"
        >
          <FontAwesomeIcon :icon="['fas', 'check-double']" class="mr-1" />
          Select
        </button>
        <ExportMenu size="sm" :disabled="!items.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
    </div>

    <!-- Bulk actions bar -->
    <div
      v-if="effectiveStaffView && bulkMode && checkedIds.size > 0"
      class="flex items-center px-4 py-2 bg-primary-50 border-b border-primary-200 gap-3"
    >
      <span class="text-xs font-medium text-primary-700">{{ checkedIds.size }} selected</span>

      <!-- Status -->
      <div class="relative" ref="bulkStatusContainer">
        <button
          class="text-[11px] font-medium px-2 py-1 rounded border border-primary-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          @click.stop="bulkDropdown = bulkDropdown === 'status' ? null : 'status'"
        >
          Status
          <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-[8px] ml-1 text-gray-400" />
        </button>
        <div
          v-if="bulkDropdown === 'status'"
          class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 z-20 min-w-[140px]"
          @click.stop
        >
          <button
            v-for="s in bulkStatusOptions"
            :key="s.value"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2"
            @click="bulkSetStatus(s.value)"
          >
            <span class="px-1.5 py-0.5 rounded" :class="statusClass(s.value)">{{ s.label }}</span>
          </button>
        </div>
      </div>

      <!-- Owner -->
      <div class="relative" ref="bulkOwnerContainer">
        <button
          class="text-[11px] font-medium px-2 py-1 rounded border border-primary-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          @click.stop="bulkDropdown = bulkDropdown === 'owner' ? null : 'owner'"
        >
          Owner
          <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-[8px] ml-1 text-gray-400" />
        </button>
        <div
          v-if="bulkDropdown === 'owner'"
          class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 z-20 min-w-[180px] max-h-48 overflow-y-auto"
          @click.stop
        >
          <button
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-400"
            @click="bulkSetOwner(null)"
          >
            Unassigned
          </button>
          <button
            v-for="user in allUsers"
            :key="user.id"
            class="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-2"
            @click="bulkSetOwner(user.id)"
          >
            <span
              class="w-4 h-4 rounded-full shrink-0 flex items-center justify-center text-[9px] font-medium text-white"
              :style="{ backgroundColor: avatarColor(user.full_name || user.email) }"
            >
              {{ initials(user.full_name || user.email) }}
            </span>
            {{ user.full_name || user.email }}
          </button>
        </div>
      </div>

      <!-- Due Date -->
      <div class="relative" ref="bulkDueDateContainer">
        <button
          class="text-[11px] font-medium px-2 py-1 rounded border border-primary-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
          @click.stop="bulkDropdown = bulkDropdown === 'due_date' ? null : 'due_date'"
        >
          Due Date
          <FontAwesomeIcon :icon="['fas', 'chevron-down']" class="text-[8px] ml-1 text-gray-400" />
        </button>
        <div
          v-if="bulkDropdown === 'due_date'"
          class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg p-3 z-20"
          @click.stop
        >
          <input
            type="date"
            class="text-xs border border-gray-300 rounded px-2 py-1.5 w-full"
            @change="bulkSetDueDate($event.target.value)"
          />
          <button
            class="w-full text-left text-xs text-gray-400 hover:text-gray-600 mt-2"
            @click="bulkSetDueDate(null)"
          >
            Clear due date
          </button>
        </div>
      </div>

      <!-- Priority -->
      <div class="flex items-center gap-1 border-l border-primary-200 pl-3">
        <button
          v-for="level in priorityLevels"
          :key="level.value"
          class="text-[11px] font-medium px-1.5 py-0.5 rounded transition-colors hover:opacity-80"
          :class="level.class"
          :style="{ backgroundColor: level.value === 0 ? '#fef2f2' : level.value === 1 ? '#fff7ed' : level.value === 2 ? '#fffbeb' : '#f3f4f6' }"
          @click="bulkSetPriority(level.value)"
        >
          {{ level.label }}
        </button>
        <button
          class="text-[11px] text-gray-400 hover:text-gray-600"
          @click="bulkSetPriority(null)"
        >
          <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-[10px]" />
        </button>
      </div>

      <button
        class="text-[11px] text-gray-400 hover:text-gray-600 ml-auto"
        @click="checkedIds = new Set()"
      >
        Deselect all
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-6 py-12 text-center">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-300 text-lg animate-spin" />
    </div>

    <!-- Board view -->
    <div v-else-if="viewMode === 'board'" class="overflow-y-auto px-4 py-4" style="max-height: calc(100vh - 220px)">
      <div class="flex gap-4 overflow-x-auto pb-4">
        <!-- Columns are a fixed width in a horizontally scrolling row, so the
             customer board gaining a fourth column widens the row rather than
             squeezing the existing three. -->
        <div
          v-for="col in boardColumns"
          :key="col.status"
          class="shrink-0 w-72 rounded-lg"
          :class="col.status === 'waiting' && isCustomerView ? 'bg-orange-50 ring-1 ring-orange-200' : 'bg-gray-50'"
        >
          <div
            class="flex items-center gap-2 px-3 py-2.5 border-b"
            :class="col.status === 'waiting' && isCustomerView ? 'border-orange-200' : 'border-gray-200'"
          >
            <span
              class="w-2 h-2 rounded-full shrink-0"
              :class="statusDotClass(col.status)"
            />
            <span
              class="text-xs font-medium"
              :class="col.status === 'waiting' && isCustomerView ? 'text-orange-900' : 'text-gray-700'"
              :title="isCustomerView ? statusTitle(col.status) : ''"
            >{{ col.label }}</span>
            <span class="text-[10px] text-gray-400 ml-auto">{{ col.items.length }}</span>
          </div>
          <div class="p-2 space-y-2 overflow-y-auto" style="max-height: calc(100vh - 320px)">
            <div
              v-for="item in col.items"
              :key="item.id"
              class="bg-white rounded-lg border border-gray-200 px-3 py-2.5 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
              :class="{ 'opacity-50': isDone(item) }"
              @click="onRowClick(item)"
            >
              <div class="flex items-start gap-2 mb-1.5">
                <span
                  class="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0 mt-0.5"
                  :class="typeClass(item.requirement_type_name)"
                >
                  {{ typeLabel(item.requirement_type_name) }}
                </span>
                <span
                  class="text-sm text-gray-900 line-clamp-2"
                  :class="{ 'line-through': isDone(item) }"
                  v-html="highlightSearch(item.title)"
                />
              </div>
              <div class="flex items-center gap-2 mt-2">
                <span
                  v-if="item.requester_name"
                  class="w-5 h-5 rounded-full shrink-0 flex items-center justify-center text-[9px] font-medium text-white"
                  :style="{ backgroundColor: avatarColor(item.requester_name) }"
                  :title="item.requester_name"
                >
                  {{ initials(item.requester_name) }}
                </span>
                <span v-if="item.due_date" class="text-[10px]" :class="dueDateClass(item.due_date)">
                  {{ dueDateLabel(item.due_date) }}
                </span>
                <span v-if="commentCounts[item.id]" class="text-[10px] text-gray-400 inline-flex items-center gap-0.5 ml-auto">
                  <FontAwesomeIcon :icon="['far', 'comment']" class="text-[9px]" />
                  {{ commentCounts[item.id] }}
                </span>
                <Support_PriorityRating
                  v-if="itemPriority(item) != null"
                  :model-value="itemPriority(item)"
                  :readonly="true"
                  class="ml-auto"
                />
              </div>
            </div>
            <div v-if="col.items.length === 0" class="text-xs text-gray-300 text-center py-6">
              No items
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table view -->
    <div v-else class="overflow-y-auto" style="max-height: calc(100vh - 220px)">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
            <th v-if="bulkMode" class="px-4 py-2.5 w-8 bg-gray-50">
              <input
                type="checkbox"
                :checked="allChecked"
                :indeterminate="someChecked && !allChecked"
                class="rounded border-gray-300 h-3 w-3"
                @change="toggleAllChecked"
              />
            </th>
            <th
              v-for="col in columns"
              :key="col.key"
              class="px-4 py-2.5 text-left text-xs font-medium text-gray-500 whitespace-nowrap select-none bg-gray-50"
            >
              <div class="flex items-center gap-1">
                <span
                  :class="col.sortable ? 'cursor-pointer hover:text-gray-700' : ''"
                  @click="col.sortable ? sortBy(col.key) : null"
                >
                  {{ col.label }}
                  <span v-if="sortField === col.key" class="ml-0.5">
                    {{ sortDir === 'asc' ? '↑' : '↓' }}
                  </span>
                </span>
                <button
                  v-if="col.filterable"
                  :ref="'filterBtn_' + col.key"
                  class="p-0.5 rounded hover:bg-gray-200 transition-colors"
                  :class="isColumnFilterActive(col.key) ? 'text-primary-500' : 'text-gray-400'"
                  @click.stop="toggleFilterDropdown(col.key)"
                >
                  <FontAwesomeIcon :icon="['fas', 'filter']" class="text-[10px]" />
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <template
            v-for="item in pagedItems"
            :key="item.id"
          >
          <tr
            :class="item._isGroup
              ? 'border-b border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100'
              : 'border-b border-gray-100 transition-colors cursor-pointer border-l-[3px] ' + statusBorderClass(item.status) + ' ' + (item._ghost ? 'opacity-50 ' : '') + (isDone(item) ? 'opacity-50 ' : '') + (detailItemId === item.id ? 'bg-primary-50' : 'hover:bg-gray-50') + (focusedItemId === item.id ? ' ring-1 ring-inset ring-primary-300' : '')"
            @click="item._isGroup ? toggleEpic(item._groupKey) : onRowClick(item)"
          >
            <template v-if="item._isGroup">
              <td :colspan="visibleColCount" class="px-4 py-2.5">
                <div class="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <FontAwesomeIcon
                    :icon="['fas', expandedEpicIds.has(item._groupKey) ? 'chevron-down' : 'chevron-right']"
                    class="text-[10px] text-gray-400"
                  />
                  {{ item.title }}
                  <span class="text-xs font-normal text-gray-400">({{ item._childCount }})</span>
                  <div v-if="item._progress" class="flex items-center gap-1.5 ml-1">
                    <div class="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        class="h-full rounded-full transition-all"
                        :class="item._progress.pct === 100 ? 'bg-green-500' : 'bg-primary-400'"
                        :style="{ width: item._progress.pct + '%' }"
                      />
                    </div>
                    <span class="text-[10px] font-normal text-gray-400">{{ item._progress.done }}/{{ item._progress.total }}</span>
                  </div>
                </div>
              </td>
            </template>
            <template v-else>
            <!-- Bulk checkbox -->
            <td v-if="bulkMode" class="px-4 py-2.5 w-8" @click.stop>
              <input
                type="checkbox"
                :checked="checkedIds.has(item.id)"
                class="rounded border-gray-300 h-3 w-3"
                @change="toggleCheck(item.id)"
              />
            </td>
            <!-- Type + Source -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <div class="flex items-center gap-1.5">
                <span
                  class="text-[11px] font-medium px-1.5 py-0.5 rounded"
                  :class="typeClass(item.requirement_type_name)"
                >
                  {{ typeLabel(item.requirement_type_name) }}
                </span>
                <SourceBadge v-if="item.source === 'email'" :source="item.source" />
              </div>
            </td>
            <!-- Title -->
            <td class="px-4 py-2.5 max-w-md">
              <div class="flex items-start" :style="{ paddingLeft: item._depth * 24 + 'px' }">
                <div
                  class="shrink-0 w-4 h-5 flex items-center justify-center mr-1"
                  :class="item.requirement_type_name === 'Epic' && item._childCount > 0 ? 'text-gray-400 hover:text-gray-600 cursor-pointer' : ''"
                  @click.stop="item.requirement_type_name === 'Epic' && item._childCount > 0 ? toggleEpic(item.id) : null"
                >
                  <FontAwesomeIcon
                    v-if="item.requirement_type_name === 'Epic' && item._childCount > 0"
                    :icon="['fas', expandedEpicIds.has(item.id) ? 'chevron-down' : 'chevron-right']"
                    class="text-[10px]"
                  />
                  <span
                    v-else-if="item._depth > 0"
                    class="text-gray-300 text-[11px] leading-none"
                  >└</span>
                </div>
                <Support_DescriptionPopover :description="item.description">
                  <div>
                    <button
                      v-if="!item._isGroup"
                      type="button"
                      class="text-[11px] font-mono mr-1.5 align-middle transition-colors"
                      :class="copiedId === item.id ? 'text-green-600' : 'text-gray-400 hover:text-primary-600'"
                      :title="copiedId === item.id ? 'Copied!' : 'Copy ticket number'"
                      :aria-label="'Copy ticket number ' + item.id"
                      @click.stop="copyTicketId(item.id)"
                    >#{{ item.id }}</button>
                    <span
                      class="text-left text-sm"
                      :class="[item._depth > 0 ? 'text-gray-600' : 'text-gray-900', isDone(item) ? 'line-through' : '']"
                      v-html="highlightSearch(item.title)"
                    />
                    <!-- Match context: shown when the search matched description or notes, not title -->
                    <span
                      v-if="searchMatchExcerpt(item)"
                      class="block text-[11px] text-gray-400 italic truncate max-w-xs mt-0.5"
                      :title="searchMatchExcerpt(item)"
                      v-html="highlightSearch(searchMatchExcerpt(item))"
                    />
                    <div v-if="item._childCount > 0 && item._progress" class="inline-flex items-center gap-1.5 ml-1.5">
                      <div class="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          class="h-full rounded-full transition-all"
                          :class="item._progress.pct === 100 ? 'bg-green-500' : 'bg-primary-400'"
                          :style="{ width: item._progress.pct + '%' }"
                        />
                      </div>
                      <span class="text-[10px] text-gray-400">{{ item._progress.done }}/{{ item._progress.total }}</span>
                    </div>
                    <span v-else-if="item._childCount > 0 && !expandedEpicIds.has(item.id)" class="text-[10px] text-gray-400 ml-1">
                      ({{ item._childCount }})
                    </span>
                    <span v-if="!item.customer_facing" class="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 ml-1.5">Internal</span>
                    <!-- Parent linkage is internal planning context: useful to staff,
                         noise to a customer looking at their own bug report. -->
                    <span
                      v-if="!isCustomerView && item.parent && epicTitles[item.parent]"
                      class="text-[10px] text-gray-400 ml-1.5"
                    >
                      · {{ epicTitles[item.parent] }}
                    </span>
                  </div>
                </Support_DescriptionPopover>
              </div>
            </td>
            <!-- Activity -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <button
                class="inline-flex items-center gap-1 text-xs transition-colors rounded px-1.5 py-0.5"
                :class="detailItemId === item.id
                  ? 'text-primary-600 bg-primary-50'
                  : commentCounts[item.id] ? 'text-gray-600 hover:text-gray-800 hover:bg-gray-100' : 'text-gray-300 hover:text-gray-500 hover:bg-gray-100'"
                @click.stop="detailItemId = item.id"
              >
                <span v-if="commentCounts[item.id]" class="relative">
                  <FontAwesomeIcon :icon="['fas', 'comment']" class="text-[11px]" />
                  <span class="absolute -top-1 -right-1.5 w-1.5 h-1.5 bg-primary-500 rounded-full" />
                </span>
                <FontAwesomeIcon v-else :icon="['far', 'comment']" class="text-[11px]" />
                <span v-if="commentCounts[item.id]">{{ commentCounts[item.id] }}</span>
              </button>
            </td>
            <!-- Status -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <!-- role="img" is load-bearing: a bare <span> is role=generic,
                   where aria-label is prohibited and dropped by every screen
                   reader, so the gloss would never be announced. -->
              <span
                class="text-xs px-2 py-0.5 rounded"
                :class="statusClass(item.status)"
                :title="statusTitle(item.status)"
                role="img"
                :aria-label="statusAriaLabel(item.status)"
              >
                {{ statusLabel(item.status) }}
              </span>
              <!-- How long we've been blocked on the customer. Staff scanning
                   aid: a ticket parked in Waiting for three weeks is otherwise
                   invisible. Gated on effectiveStaffView so it doesn't leak
                   through the Customer view preview. -->
              <span
                v-if="staleWaitingLabel(item)"
                class="ml-1 text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-800 align-middle"
                :title="'Blocked on the customer since ' + formatDate(item.waiting_since) + ' (' + staleWaitingLabel(item) + ')'"
                role="img"
                :aria-label="'Blocked on the customer for ' + staleWaitingLabel(item)"
              >
                {{ staleWaitingLabel(item) }}
              </span>
            </td>
            <!-- Requester -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <div v-if="item.requester_name" class="flex items-center gap-1.5">
                <span
                  class="w-5 h-5 rounded-full shrink-0 flex items-center justify-center text-[10px] font-medium text-white"
                  :style="{ backgroundColor: avatarColor(item.requester_name) }"
                >
                  {{ initials(item.requester_name) }}
                </span>
                <span class="text-xs text-gray-600 truncate max-w-[100px]">{{ item.requester_name }}</span>
              </div>
            </td>
            <!-- Due Date / Completed Date -->
            <td v-if="isDone(item)" class="px-4 py-2.5 whitespace-nowrap text-xs" :title="'Completed ' + relativeTime(item.completed_at || item.updated_at)">
              <span class="inline-flex items-center gap-1 text-green-600">
                <FontAwesomeIcon :icon="['fas', 'check-circle']" class="text-[10px]" />
                {{ formatDate(item.completed_at || item.updated_at) }}
              </span>
            </td>
            <td v-else class="px-4 py-2.5 whitespace-nowrap text-xs" :title="relativeDueDate(item.due_date)">
              <span v-if="!item.due_date" class="text-gray-300">--</span>
              <span v-else class="inline-flex items-center gap-1" :class="dueDateClass(item.due_date)">
                <FontAwesomeIcon v-if="isDueSoon(item.due_date)" :icon="['fas', 'clock']" class="text-[10px]" />
                {{ dueDateLabel(item.due_date) }}
              </span>
            </td>
            <!-- Updated -->
            <td class="px-4 py-2.5 whitespace-nowrap text-xs text-gray-400" :title="formatDate(item.updated_at)">
              {{ relativeTime(item.updated_at) }}
            </td>
            <!-- Requested (created_at) -->
            <td class="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500" :title="item.created_at ? formatDate(item.created_at) : ''">
              {{ item.created_at ? formatDate(item.created_at) : '--' }}
            </td>
            <!-- Priority — read-only for customers (visible, no edit), editable for staff -->
            <td class="px-4 py-2.5 whitespace-nowrap">
              <Support_PriorityRating
                :model-value="itemPriority(item)"
                :readonly="isCustomerView"
                @update:model-value="setPriority(item, $event)"
              />
            </td>
            </template>
          </tr>
          </template>
          <tr v-if="filteredItems.length === 0">
            <td :colspan="visibleColCount" class="px-4 py-12 text-center">
              <div v-if="!search && !hasActiveColumnFilters && items.length === 0" class="py-8 max-w-sm mx-auto">
                <div class="flex justify-center gap-3 mb-5 text-gray-200">
                  <FontAwesomeIcon :icon="['fas', 'bug']" class="text-2xl" />
                  <FontAwesomeIcon :icon="['fas', 'lightbulb']" class="text-3xl -mt-1" />
                  <FontAwesomeIcon :icon="['fas', 'ticket']" class="text-2xl" />
                </div>
                <p class="text-sm font-medium text-gray-700 mb-1">No requests yet</p>
                <p class="text-xs text-gray-400 mb-5">
                  This is where you'll track bugs, feature requests, and support tickets.
                  Our team reviews every submission and will follow up directly.
                </p>
                <div class="flex justify-center gap-2">
                  <button
                    class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                    @click="openCreate('Bug')"
                  >
                    <FontAwesomeIcon :icon="['fas', 'bug']" class="text-[10px]" />
                    Report a Bug
                  </button>
                  <button
                    class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                    @click="openCreate('Support Ticket')"
                  >
                    <FontAwesomeIcon :icon="['fas', 'ticket']" class="text-[10px]" />
                    Submit a Ticket
                  </button>
                  <button
                    class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    @click="openCreate('Feature')"
                  >
                    <FontAwesomeIcon :icon="['fas', 'lightbulb']" class="text-[10px]" />
                    Request a Feature
                  </button>
                </div>
              </div>
              <div v-else-if="search" class="py-6 max-w-xs mx-auto">
                <FontAwesomeIcon :icon="['fas', 'search']" class="text-gray-200 text-xl mb-3" />
                <p class="text-sm text-gray-500 mb-1">No results for "{{ search }}"</p>
                <p class="text-xs text-gray-400 mb-4">Try a different search term, or start over.</p>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  @click="resetAll"
                >
                  <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-[10px]" />
                  Clear search and filters
                </button>
              </div>
              <!-- "My Open" preset with no open submissions from this user -->
              <div v-else-if="activePreset === 'my_open'" class="py-8 max-w-sm mx-auto text-center">
                <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-gray-200 text-2xl mb-3" />
                <p class="text-sm font-medium text-gray-700 mb-1">You're all caught up</p>
                <p class="text-xs text-gray-400 mb-4">No open requests from you right now.</p>
                <div class="flex justify-center gap-2">
                  <button class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors" @click="openCreate('Bug')">
                    <FontAwesomeIcon :icon="['fas', 'bug']" class="text-[10px]" /> Report a Bug
                  </button>
                  <button class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors" @click="openCreate('Support Ticket')">
                    <FontAwesomeIcon :icon="['fas', 'ticket']" class="text-[10px]" /> Submit a Ticket
                  </button>
                </div>
              </div>

              <!-- "Needs Input" preset with no waiting tickets -->
              <div v-else-if="activePreset === 'needs_input'" class="py-8 max-w-xs mx-auto text-center">
                <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-gray-200 text-2xl mb-3" />
                <p class="text-sm font-medium text-gray-700 mb-1">Nothing needs your input right now</p>
                <p class="text-xs text-gray-400">We'll email you when we need more information on a ticket.</p>
              </div>

              <div v-else class="py-6">
                <FontAwesomeIcon :icon="['fas', 'filter']" class="text-gray-200 text-xl mb-3" />
                <p class="text-sm text-gray-500 mb-1">No items match your filters</p>
                <p class="text-xs text-gray-400 mb-4">
                  {{ items.length }} {{ items.length === 1 ? 'item is' : 'items are' }} hidden by the filters above.
                </p>
                <button
                  type="button"
                  class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                  @click="resetAll"
                >
                  <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-[10px]" />
                  Clear filters
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Filter dropdown (fixed position to escape overflow) -->
    <Teleport to="body">
      <div
        v-if="openFilterCol"
        class="fixed z-50 bg-white border border-gray-200 rounded shadow-lg py-1 min-w-[180px]"
        :style="filterDropdownStyle"
        @click.stop
      >
        <!-- Due Date filter -->
        <template v-if="openFilterCol === 'due_date'">
          <div class="px-3 py-2 space-y-2">
            <label class="block text-xs text-gray-500">From</label>
            <input
              type="date"
              :value="columnFilters.due_date_from"
              class="w-full text-xs border border-gray-300 rounded px-2 py-1"
              @input="columnFilters = { ...columnFilters, due_date_from: $event.target.value }"
            />
            <label class="block text-xs text-gray-500">To</label>
            <input
              type="date"
              :value="columnFilters.due_date_to"
              class="w-full text-xs border border-gray-300 rounded px-2 py-1"
              @input="columnFilters = { ...columnFilters, due_date_to: $event.target.value }"
            />
          </div>
        </template>

        <!-- Clear button -->
        <button
          v-if="isColumnFilterActive(openFilterCol)"
          class="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 mt-1"
          @click="clearColumnFilter(openFilterCol)"
        >
          Clear filter
        </button>
      </div>
    </Teleport>

    <!-- Full filter panel -->
    <Teleport to="body">
      <div
        v-if="showFilterPanel"
        class="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-xl w-[420px]"
        :style="filterPanelStyle"
        @click.stop
      >
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">Filters</h3>
          <button class="text-gray-400 hover:text-gray-600 transition-colors" @click="showFilterPanel = false">
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>

        <div class="px-4 py-3 space-y-3">
          <!-- Active filter rows -->
          <div v-for="key in filterPanelRows" :key="key">
            <div class="flex items-center gap-2">
              <span class="w-20 shrink-0 text-xs font-medium text-gray-600">{{ filterFieldLabel(key) }}</span>
              <span class="text-xs text-gray-400 shrink-0">is</span>
              <button
                class="flex-1 text-left text-xs border rounded px-2.5 py-1.5 truncate transition-colors"
                :class="expandedFilterField === key
                  ? 'border-primary-400 ring-1 ring-primary-200 bg-white'
                  : 'border-gray-200 hover:border-gray-300 bg-white'"
                @click.stop="expandedFilterField = expandedFilterField === key ? null : key"
              >
                {{ filterValueSummary(key) }}
              </button>
              <button class="shrink-0 text-gray-300 hover:text-gray-500 transition-colors" @click="clearFilterFromPanel(key)">
                <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xs" />
              </button>
            </div>

            <!-- Inline value picker (expands below the row) -->
            <div
              v-if="expandedFilterField === key"
              class="mt-1 ml-[88px] border border-gray-100 rounded bg-gray-50 max-h-44 overflow-y-auto"
            >
              <!-- Type -->
              <template v-if="key === 'type'">
                <label
                  v-for="opt in visibleTypeFilters"
                  :key="opt.key"
                  class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white cursor-pointer"
                >
                  <input type="checkbox" :checked="isTypeFilterActive(opt)" class="rounded border-gray-300" @change="toggleType(opt)" />
                  <span class="px-1.5 py-0.5 rounded" :class="typeClass(opt.types[opt.types.length - 1])">{{ opt.label }}</span>
                </label>
              </template>

              <!-- Status -->
              <template v-if="key === 'status'">
                <label
                  v-for="opt in statusFilterOptions"
                  :key="opt.value"
                  class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white cursor-pointer"
                >
                  <input type="checkbox" :checked="columnFilters.status.has(opt.value)" class="rounded border-gray-300" @change="toggleColumnFilter('status', opt.value)" />
                  <span class="px-1.5 py-0.5 rounded" :class="statusClass(opt.value)">{{ opt.label }}</span>
                </label>
              </template>

              <!-- Requester -->
              <template v-if="key === 'requester'">
                <div class="px-2 pt-1.5 pb-0.5 sticky top-0 bg-gray-50">
                  <input
                    v-model="panelRequesterSearch"
                    type="text"
                    placeholder="Find person..."
                    class="w-full text-xs border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-primary-400"
                    @click.stop
                  />
                </div>
                <label
                  v-for="opt in requesterFilterOptions.filter(o =>
                    !panelRequesterSearch || o.name.toLowerCase().includes(panelRequesterSearch.toLowerCase())
                  )"
                  :key="String(opt.id)"
                  class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white cursor-pointer"
                >
                  <input type="checkbox" :checked="columnFilters.requester.has(opt.id)" class="rounded border-gray-300" @change="toggleColumnFilter('requester', opt.id)" />
                  <span class="truncate">{{ opt.name }}</span>
                </label>
                <p v-if="panelRequesterSearch && requesterFilterOptions.filter(o => o.name.toLowerCase().includes(panelRequesterSearch.toLowerCase())).length === 0" class="px-3 py-2 text-xs text-gray-400">No match</p>
              </template>

              <!-- Priority -->
              <template v-if="key === 'priority'">
                <label v-for="level in priorityLevels" :key="level.value" class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white cursor-pointer">
                  <input type="checkbox" :checked="columnFilters.priority_score.has(level.value)" class="rounded border-gray-300" @change="toggleColumnFilter('priority_score', level.value)" />
                  <span class="font-medium" :class="level.class">{{ level.label }}</span>
                </label>
                <label class="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white cursor-pointer">
                  <input type="checkbox" :checked="columnFilters.priority_score.has('unset')" class="rounded border-gray-300" @change="toggleColumnFilter('priority_score', 'unset')" />
                  <span class="text-gray-400">Not rated</span>
                </label>
              </template>
            </div>
          </div>

          <!-- Add filter -->
          <div class="relative">
            <button
              class="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              @click.stop="filterPanelAddMenu = !filterPanelAddMenu"
            >
              <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[9px]" />
              Add filter
            </button>
            <div
              v-if="filterPanelAddMenu"
              class="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-10 min-w-[140px] py-1"
              @click.stop
            >
              <button
                v-for="field in availableFilterFields"
                :key="field.key"
                class="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                @click="addFilterToPanel(field.key)"
              >
                {{ field.label }}
              </button>
              <p v-if="availableFilterFields.length === 0" class="px-3 py-2 text-xs text-gray-400">All filters active</p>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Pagination -->
    <div
      v-if="totalPages > 1"
      class="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-xs text-gray-500"
    >
      <span>
        {{ (currentPage - 1) * pageSize + 1 }}–{{ Math.min(currentPage * pageSize, filteredItems.length) }}
        of {{ filteredItems.length }}
      </span>
      <div class="flex gap-1">
        <button
          v-for="page in pageRange"
          :key="page"
          class="px-2 py-1 rounded"
          :class="page === currentPage ? 'bg-primary-500 text-white' : 'hover:bg-gray-100'"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
      </div>
    </div>

    <!-- Detail tray -->
    <Support_ItemDetail
      :item-id="detailItemId"
      :force-customer-view="isCustomerView"
      :users="allUsers"
      @close="detailItemId = null"
      @comment-count="onTrayCommentCount"
      @triaged="onTicketTriaged"
      @updated="onTrayUpdated"
    />

    <!-- Create modal -->
    <Support_CreateModal
      v-if="showCreateModal"
      :requirement-types="allRequirementTypes"
      :users="allUsers"
      :parent-options="items"
      :tenant-options="tenantOptions"
      :customer-facing="true"
      :default-type-name="createDefaultType"
      @close="showCreateModal = false"
      @created="onItemCreated"
    />
    <Support_UndoToast ref="undoToast" />
  </div>
</template>

<script>
import axios from 'axios';
import { z } from 'zod';
import { mapGetters } from 'vuex';
import { BrowserStore } from '@/utils/BrowserStore';
import Support_PriorityRating from './Support_PriorityRating.vue';
import Support_DescriptionPopover from './Support_DescriptionPopover.vue';
import Support_CreateModal from './Support_CreateModal.vue';
import Support_ItemDetail from './Support_ItemDetail.vue';
import Support_UndoToast from './Support_UndoToast.vue';
import SourceBadge from './SourceBadge.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import {
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUS_CLASSES,
  CUSTOMER_STATUS_ORDER,
  CUSTOMER_STATUS_HINTS,
  customerBucket,
  customerTypeLabel,
  formatDateValue,
  daysUntil,
  daysAgoShortLabel,
  evictStaleItemCaches,
  SELECTABLE_STATUSES,
  STATUS_LABELS,
  matchesSearch,
} from './support_constants';

const VISIBLE_TYPES = new Set(['Epic', 'Feature', 'Bug', 'Support Ticket']);
const DONE_STATUSES = new Set(['done', 'resolved', 'closed']);
// Bugs and Support Tickets always live under "Support & Bugs", whatever they're
// parented to. Parenting is an internal planning link; it must not move a ticket
// out of the section the customer looks for it in.
const SUPPORT_TYPES = new Set(['Bug', 'Support Ticket']);

// Bump whenever a persisted filter value changes meaning. v2: 'waiting' split
// out of the customer 'in_progress' bucket.
const FILTER_SCHEMA_VERSION = 2;

const STATUS_CLASSES = {
  approved: 'bg-blue-50 text-blue-700',
  in_progress: 'bg-amber-50 text-amber-700',
  open: 'bg-red-50 text-red-700',
  waiting: 'bg-orange-50 text-orange-700',
  done: 'bg-green-50 text-green-700',
  resolved: 'bg-green-50 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

const PRIORITY_MAP = { p0: 0, p1: 1, p2: 2, p3: 3, none: null };

function priorityOf(item) {
  if (typeof item.priority === 'number') return item.priority;
  return PRIORITY_MAP[item.priority] ?? null;
}

function defaultSort(arr) {
  return arr.sort((a, b) => {
    const pa = priorityOf(a);
    const pb = priorityOf(b);
    if (pa !== pb) {
      if (pa == null) return 1;
      if (pb == null) return -1;
      return pa - pb;
    }
    const da = a._effectiveDueDate || a.due_date || '';
    const db = b._effectiveDueDate || b.due_date || '';
    if (da !== db) {
      if (!da) return 1;
      if (!db) return -1;
      return da < db ? -1 : 1;
    }
    return 0;
  });
}

const TYPE_CLASSES = {
  'Epic': 'bg-indigo-50 text-indigo-700',
  'Feature': 'bg-blue-50 text-blue-700',
  'Bug': 'bg-red-50 text-red-700',
  'Support Ticket': 'bg-amber-50 text-amber-700',
};

// v2: entries written before the list became tenant_scoped hold a cross-tenant
// payload. filterItems() short-circuits its tenant check for staff, so reading
// one would paint another tenant's tickets under this tenant's desk -- and
// leave them there if the refresh then failed. Bumping the prefix makes those
// entries unreadable; DEAD_CACHE_PREFIXES sweeps them.
const CACHE_KEY_PREFIX = 'service_desk_items_v2';
// Loose on purpose: requirement shape is owned by the backend and evolves
// independently of this cache. We only need "an array of objects" to keep a
// malformed/truncated response from poisoning items — see BrowserStore.
// Wrapped in an object because BrowserStore.load()'s invalid-data fallback is
// schema.parse({}), which only resolves to defaults for an object-root schema.
const ITEMS_CACHE_SCHEMA =
  z.object({ items: z.array(z.record(z.string(), z.unknown())).default([]) });

function cacheKeyFor(tenantSlug, userId) {
  if (!tenantSlug || userId == null) return null;
  return `${CACHE_KEY_PREFIX}:${tenantSlug}:${userId}`;
}

export default {
  name: 'Support_ServiceDesk',
  components: { Support_PriorityRating, Support_DescriptionPopover, Support_CreateModal, Support_ItemDetail, Support_UndoToast, SourceBadge, ExportMenu },
  props: {
    selectedId: { type: [String, Number], default: null },
    selectedTitle: { type: String, default: '' },
  },
  emits: ['select'],
  data() {
    return {
      loading: true,
      items: [],
      viewMode: 'table',
      search: '',
      refreshing: false,
      expandedEpicIds: new Set(),
      commentTypeId: null,
      sortField: null,
      sortDir: 'asc',
      columnFilters: {
        requirement_type_name: new Set(),
        status: new Set(),
        due_date_from: '',
        due_date_to: '',
        priority_score: new Set(),
        requester: new Set(),
      },
      filterDropdownStyle: {},
      openFilterCol: null,
      activePreset: null,
      showFilterPanel: false,
      filterPanelKeys: [],
      filterPanelAddMenu: false,
      expandedFilterField: null,
      filterPanelStyle: {},
      panelRequesterSearch: '',
      currentPage: 1,
      pageSize: 50,
      columns: [
        { key: 'requirement_type_name', label: 'Type', sortable: true, filterable: false },
        { key: 'title', label: 'Title', sortable: true },
        { key: 'comments', label: '', sortable: false, filterable: false },
        { key: 'status', label: 'Status', sortable: true, filterable: false },
        { key: 'requester_name', label: 'Requester', sortable: true, filterable: false },
        { key: 'due_date', label: 'Due Date', sortable: true, filterable: true },
        { key: 'updated_at', label: 'Updated', sortable: true },
        { key: 'created_at', label: 'Requested', sortable: true },
        { key: 'priority_score', label: 'Priority', sortable: false, filterable: false },
      ],
      // Chip filters. `types` is the set of internal requirement types a chip
      // stands for, so one chip can cover several types — see
      // visibleTypeFilters for why the customer's "Feature Requests" needs to.
      typeFilters: [
        { key: 'epic', label: 'Epics', types: ['Epic'] },
        { key: 'feature', label: 'Features', types: ['Feature'] },
        { key: 'bug', label: 'Bugs', types: ['Bug'] },
        { key: 'ticket', label: 'Tickets', types: ['Support Ticket'] },
      ],
      // P0-P3 only — the old "P4" was a phantom (no backend p4 code) that sent
      // priority=4 and 400'd (Item 1).
      priorityLevels: [
        { value: 0, label: 'P0', class: 'text-red-700' },
        { value: 1, label: 'P1', class: 'text-orange-700' },
        { value: 2, label: 'P2', class: 'text-amber-700' },
        { value: 3, label: 'P3', class: 'text-gray-600' },
      ],
      commentCounts: {},
      bulkMode: false,
      checkedIds: new Set(),
      focusedIndex: -1,
      showCreateModal: false,
      createDefaultType: null,
      allRequirementTypes: [],
      allUsers: [],
      detailItemId: null,
      bulkDropdown: null,
      // Persisted so a reload mid screen-share doesn't silently drop the preview.
      customerPreview: sessionStorage.getItem('service_desk_customer_preview') === '1',
      copiedId: null,
      lastFetchedAt: null,
      fetchError: false,
      retryAttempt: 0,
      now: Date.now(),
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_name', 'current_tenant_slug']),
    ...mapGetters('auth', ['current_user']),
    headerTitle() {
      if (this.selectedId == null) return 'Service Desk';
      const title = this.selectedTitle.trim();
      return title
        ? `Service Desk #${this.selectedId} — ${title}`
        : `Service Desk #${this.selectedId}`;
    },
    isStaffUser() {
      // Kairon staff. Mirrors the backend's dividing line (non-staff users are
      // tenant- and customer_facing-scoped), and matches Support_ItemDetail /
      // RequirementCommentThread, so the staff/customer split is consistent
      // across data scoping AND every UI affordance.
      return !!(this.current_user?.is_staff || this.current_user?.is_superuser);
    },
    // Staff can flip a "Customer view" preview (item 7) to see exactly what a
    // customer sees — internal rows hidden, customer labels, no staff
    // affordances — the clean screen-share view. Non-staff are always customer.
    effectiveStaffView() {
      return this.isStaffUser && !this.customerPreview;
    },
    isCustomerView() {
      return !this.effectiveStaffView;
    },
    updatedLabel() {
      if (!this.lastFetchedAt) return '';
      const diffSec = Math.max(0, Math.round((this.now - this.lastFetchedAt) / 1000));
      if (diffSec < 10) return 'Updated just now';
      if (diffSec < 60) return `Updated ${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `Updated ${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      return `Updated ${diffHr}h ago`;
    },
    tenantOptions() {
      const seen = new Map();
      for (const item of this.items) {
        if (item.tenant && !seen.has(item.tenant)) {
          seen.set(item.tenant, { id: item.tenant, name: item.tenant_name });
        }
      }
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    epicTitles() {
      const map = {};
      for (const item of this.items) {
        if (item.requirement_type_name === 'Epic') map[item.id] = item.title;
      }
      return map;
    },
    activeTypes() {
      // Only consulted when the set is non-empty (filteredItems guards on
      // size), so an empty set means "no type filter" rather than "all types".
      return this.columnFilters.requirement_type_name;
    },
    typeFilterOptions() {
      // Mirrors the chips exactly (same groups, same labels, same toggle), so
      // the column filter can't build a selection the chips can't display —
      // and so customers never see the raw "Epic" jargon here either.
      return this.visibleTypeFilters;
    },
    statusFilterOptions() {
      const statuses = [...new Set(this.items.map((i) => i.status))].filter((s) => s !== 'draft');
      if (this.isCustomerView) {
        const buckets = new Set(statuses.map((s) => customerBucket(s)));
        return CUSTOMER_STATUS_ORDER
          .filter((b) => buckets.has(b))
          .map((b) => ({ value: b, label: CUSTOMER_STATUS_LABELS[b] }));
      }
      return statuses.sort().map((s) => ({ value: s, label: STATUS_LABELS[s] || s }));
    },
    // Anything that hides rows the user would otherwise see. Sort alone doesn't
    // count -- it reorders, it doesn't narrow.
    isNarrowed() {
      return !!this.search || this.hasActiveColumnFilters || !!this.activePreset;
    },
    activeFilterCount() {
      const f = this.columnFilters;
      let n = 0;
      if (f.requirement_type_name.size > 0) n++;
      if (f.status.size > 0) n++;
      if (f.requester.size > 0) n++;
      if (f.priority_score.size > 0) n++;
      return n;
    },
    filterPanelRows() {
      return this.filterPanelKeys;
    },
    availableFilterFields() {
      const active = new Set(this.filterPanelKeys);
      return [
        { key: 'type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'requester', label: 'Requester' },
        { key: 'priority', label: 'Priority' },
      ].filter((f) => !active.has(f.key));
    },
    hiddenCount() {
      return this.items.length - this.filteredItems.length;
    },
    // resetAll() clears filters AND sort, so the label has to name both when
    // both are set — "Clear filters" alone would quietly drop the user's sort.
    resetLabel() {
      if (this.isNarrowed && this.sortField) return 'Clear filters & sort';
      return this.isNarrowed ? 'Clear filters' : 'Reset sort';
    },
    hasActiveColumnFilters() {
      const f = this.columnFilters;
      return f.requirement_type_name.size > 0 || f.status.size > 0 || f.priority_score.size > 0 || f.requester.size > 0 || f.due_date_from || f.due_date_to;
    },
    filterState() {
      return {
        search: this.search,
        sortField: this.sortField,
        sortDir: this.sortDir,
        requirement_type_name: this.columnFilters.requirement_type_name,
        status: this.columnFilters.status,
        due_date_from: this.columnFilters.due_date_from,
        due_date_to: this.columnFilters.due_date_to,
        priority_score: this.columnFilters.priority_score,
        requester: this.columnFilters.requester,
      };
    },
    // True when the current search query is a pure ticket-id lookup (#123 or 123).
    // Used to show the exact-match hint and tint the search box.
    isIdSearch() {
      return !!this.search && /^#?\d+$/.test(this.search.trim());
    },
    requesterFilterOptions() {
      const seen = new Map();
      for (const item of this.items) {
        if (item.requester != null && !seen.has(item.requester)) {
          seen.set(item.requester, { id: item.requester, name: item.requester_name || '' });
        }
        // Tickets from inbound email may have no requester row.
        if (item.requester == null && !seen.has(null)) {
          seen.set(null, { id: null, name: 'Unknown' });
        }
      }
      return [...seen.values()].sort((a, b) => {
        if (a.id == null) return 1;
        if (b.id == null) return -1;
        return a.name.localeCompare(b.name);
      });
    },
    filteredItems() {
      let result = this.items;
      if (this.columnFilters.requirement_type_name.size > 0) {
        result = result.filter((i) => this.activeTypes.has(i.requirement_type_name));
      }
      if (this.columnFilters.status.size > 0) {
        result = this.isCustomerView
          ? result.filter((i) => this.columnFilters.status.has(customerBucket(i.status)))
          : result.filter((i) => this.columnFilters.status.has(i.status));
      }
      if (this.columnFilters.due_date_from) {
        result = result.filter((i) => i.due_date && i.due_date >= this.columnFilters.due_date_from);
      }
      if (this.columnFilters.due_date_to) {
        result = result.filter((i) => i.due_date && i.due_date <= this.columnFilters.due_date_to);
      }
      if (this.columnFilters.priority_score.size > 0) {
        result = result.filter((i) => {
          const p = this.itemPriority(i);
          if (p == null) return this.columnFilters.priority_score.has('unset');
          return this.columnFilters.priority_score.has(p);
        });
      }
      if (this.columnFilters.requester.size > 0) {
        result = result.filter((i) => this.columnFilters.requester.has(i.requester ?? null));
      }
      if (this.activePreset === 'my_open') {
        const uid = this.current_user?.id;
        if (uid) result = result.filter((i) => i.requester === uid && !DONE_STATUSES.has(i.status));
      }
      if (this.activePreset === 'needs_input') {
        result = result.filter((i) => i.status === 'waiting');
      }
      if (this.search) {
        const q = this.search.trim();
        const ranked = [];
        for (const i of result) {
          const rank = matchesSearch(i, q);
          if (rank != null) ranked.push({ item: i, rank });
        }
        ranked.sort((a, b) => a.rank - b.rank);
        result = ranked.map((r) => r.item);
      }
      return result;
    },
    treeItems() {
      const items = this.filteredItems;
      const allItemsById = {};
      for (const item of this.items) allItemsById[item.id] = item;

      const openItems = items.filter((i) => !DONE_STATUSES.has(i.status));
      const doneItems = items.filter((i) => DONE_STATUSES.has(i.status));

      const epicIds = new Set(openItems.filter((i) => i.requirement_type_name === 'Epic').map((i) => i.id));
      // Needed before the open loop: an Epic that renders in the Completed group
      // must not also be synthesized as a ghost above it (see the ghost branch).
      const completedEpicIds = new Set(doneItems.filter((i) => i.requirement_type_name === 'Epic').map((i) => i.id));
      const epics = [];
      const childrenByParent = {};
      const orphanFeatures = [];
      const orphanBugs = [];
      const orphanTickets = [];
      const ghostEpicIds = new Set();

      for (const item of openItems) {
        if (item.requirement_type_name === 'Epic') {
          epics.push(item);
        } else if (SUPPORT_TYPES.has(item.requirement_type_name)) {
          // Type wins over parent: an Epic-parented bug still belongs to
          // "Support & Bugs", never nested under the Epic (see SUPPORT_TYPES).
          if (item.requirement_type_name === 'Bug') orphanBugs.push(item);
          else orphanTickets.push(item);
        } else if (item.parent && epicIds.has(item.parent)) {
          if (!childrenByParent[item.parent]) childrenByParent[item.parent] = [];
          childrenByParent[item.parent].push(item);
        } else if (
          item.parent &&
          allItemsById[item.parent]?.requirement_type_name === 'Epic' &&
          !epicIds.has(item.parent) &&
          // A completed Epic already renders inside the Completed group. Ghosting
          // it here too would put the same id on two rows and duplicate the
          // v-for :key; let the open child stand on its own instead.
          !completedEpicIds.has(item.parent)
        ) {
          const parentEpic = allItemsById[item.parent];
          if (!ghostEpicIds.has(parentEpic.id)) {
            ghostEpicIds.add(parentEpic.id);
            epicIds.add(parentEpic.id);
            epics.push({ ...parentEpic, _ghost: true });
          }
          if (!childrenByParent[parentEpic.id]) childrenByParent[parentEpic.id] = [];
          childrenByParent[parentEpic.id].push(item);
        } else {
          // Only Feature reaches here: filterItems() gates the list to
          // VISIBLE_TYPES and every other type is handled above.
          orphanFeatures.push(item);
        }
      }

      for (const key of Object.keys(childrenByParent)) defaultSort(childrenByParent[key]);
      for (const epic of epics) {
        const children = childrenByParent[epic.id] || [];
        const dates = children.map((c) => c.due_date).filter(Boolean);
        if (dates.length) epic._effectiveDueDate = dates.sort()[0];
      }
      defaultSort(epics);
      defaultSort(orphanFeatures);
      defaultSort(orphanBugs);
      defaultSort(orphanTickets);

      const completedEpics = doneItems
        .filter((i) => i.requirement_type_name === 'Epic')
        .sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at));
      const completedChildrenByParent = {};
      const completedOrphans = [];

      for (const item of doneItems) {
        if (item.requirement_type_name === 'Epic') continue;
        // Same rule as the open tree: support types never nest under an Epic.
        if (SUPPORT_TYPES.has(item.requirement_type_name)) {
          completedOrphans.push(item);
        } else if (item.parent && completedEpicIds.has(item.parent)) {
          if (!completedChildrenByParent[item.parent]) completedChildrenByParent[item.parent] = [];
          completedChildrenByParent[item.parent].push(item);
        } else {
          completedOrphans.push(item);
        }
      }
      completedOrphans.sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at));

      return {
        epics, childrenByParent, orphanFeatures, orphanBugs, orphanTickets,
        completedEpics, completedChildrenByParent, completedOrphans,
      };
    },
    flatVisibleItems() {
      const {
        epics, childrenByParent, orphanFeatures, orphanBugs, orphanTickets,
        completedEpics, completedChildrenByParent, completedOrphans,
      } = this.treeItems;
      const result = [];

      for (const epic of epics) {
        const children = childrenByParent[epic.id] || [];
        // Deliberately reads this.items (unfiltered) so progress doesn't shift as
        // the user changes filters. Support types are excluded because they no
        // longer render as children of the Epic.
        const allChildren = this.items.filter(
          (i) => i.parent === epic.id && i.requirement_type_name !== 'Epic' && !SUPPORT_TYPES.has(i.requirement_type_name)
        );
        const doneCount = allChildren.filter(i => DONE_STATUSES.has(i.status)).length;
        const progress = allChildren.length > 0 ? { done: doneCount, total: allChildren.length, pct: Math.round((doneCount / allChildren.length) * 100) } : null;
        result.push({ ...epic, _depth: 0, _childCount: children.length, _isGroup: false, _progress: progress });
        if (this.expandedEpicIds.has(epic.id)) {
          for (const child of children) {
            result.push({ ...child, _depth: 1, _childCount: 0, _isGroup: false });
          }
        }
      }

      for (const item of orphanFeatures) {
        result.push({ ...item, _depth: 0, _childCount: 0, _isGroup: false });
      }

      const supportItems = [...orphanBugs, ...orphanTickets];
      if (supportItems.length > 0) {
        result.push({
          id: '__support__',
          title: 'Support & Bugs',
          _isGroup: true,
          _childCount: supportItems.length,
          _depth: 0,
          _groupKey: '__support__',
        });
        if (this.expandedEpicIds.has('__support__')) {
          for (const item of supportItems) {
            result.push({ ...item, _depth: 1, _childCount: 0, _isGroup: false });
          }
        }
      }

      const totalCompleted = completedEpics.length
        + Object.values(completedChildrenByParent).reduce((sum, arr) => sum + arr.length, 0)
        + completedOrphans.length;

      if (totalCompleted > 0) {
        result.push({
          id: '__completed__',
          title: `Completed (${totalCompleted})`,
          _isGroup: true,
          _childCount: totalCompleted,
          _depth: 0,
          _groupKey: '__completed__',
        });
        if (this.expandedEpicIds.has('__completed__')) {
          for (const epic of completedEpics) {
            const children = completedChildrenByParent[epic.id] || [];
            const allChildren = this.items.filter(
              (i) => i.parent === epic.id && i.requirement_type_name !== 'Epic' && !SUPPORT_TYPES.has(i.requirement_type_name)
            );
            const dCount = allChildren.filter(i => DONE_STATUSES.has(i.status)).length;
            const prog = allChildren.length > 0 ? { done: dCount, total: allChildren.length, pct: Math.round((dCount / allChildren.length) * 100) } : null;
            result.push({ ...epic, _depth: 1, _childCount: children.length, _isGroup: false, _progress: prog });
            if (this.expandedEpicIds.has(epic.id)) {
              for (const child of children) {
                result.push({ ...child, _depth: 2, _childCount: 0, _isGroup: false });
              }
            }
          }
          for (const item of completedOrphans) {
            result.push({ ...item, _depth: 1, _childCount: 0, _isGroup: false });
          }
        }
      }

      return result;
    },
    sortedItems() {
      if (!this.sortField) {
        return this.flatVisibleItems;
      }
      const all = this.filteredItems;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      return [...all].sort((a, b) => {
        if (this.sortField === 'due_date') {
          const aDate = this.effectiveDate(a);
          const bDate = this.effectiveDate(b);
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1 * dir;
          if (!bDate) return -1 * dir;
          return (new Date(aDate) - new Date(bDate)) * dir;
        }
        const av = a[this.sortField] ?? '';
        const bv = b[this.sortField] ?? '';
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      }).map((i) => ({ ...i, _depth: 0, _childCount: 0 }));
    },
    allExpandableKeys() {
      const { epics, childrenByParent, orphanBugs, orphanTickets, completedEpics, completedChildrenByParent, completedOrphans } = this.treeItems;
      const keys = epics.filter((e) => (childrenByParent[e.id] || []).length > 0).map((e) => e.id);
      if (orphanBugs.length || orphanTickets.length) keys.push('__support__');
      const totalCompleted = completedEpics.length + Object.values(completedChildrenByParent).reduce((sum, arr) => sum + arr.length, 0) + completedOrphans.length;
      if (totalCompleted > 0) keys.push('__completed__');
      for (const e of completedEpics) {
        if ((completedChildrenByParent[e.id] || []).length > 0) keys.push(e.id);
      }
      return keys;
    },
    allExpanded() {
      return this.allExpandableKeys.length > 0 && this.allExpandableKeys.every((k) => this.expandedEpicIds.has(k));
    },
    focusedItemId() {
      if (this.focusedIndex < 0) return null;
      const items = this.pagedItems.filter((i) => !i._isGroup);
      return items[this.focusedIndex]?.id ?? null;
    },
    boardColumns() {
      if (this.isCustomerView) {
        // Keyed off CUSTOMER_STATUS_ORDER so a new bucket ('waiting') gets its
        // column automatically instead of having its tickets silently dropped.
        const byBucket = {};
        for (const b of CUSTOMER_STATUS_ORDER) byBucket[b] = [];
        for (const item of this.filteredItems) {
          if (item.requirement_type_name === 'Epic') continue;
          const b = customerBucket(item.status || 'open');
          if (byBucket[b]) byBucket[b].push(item);
        }
        return CUSTOMER_STATUS_ORDER.map((b) => ({
          status: b,
          label: CUSTOMER_STATUS_LABELS[b],
          items: byBucket[b],
        }));
      }
      const order = ['open', 'approved', 'in_progress', 'waiting', 'done', 'resolved', 'closed'];
      const labels = { open: 'Open', approved: 'Approved', in_progress: 'In Progress', waiting: 'Waiting', done: 'Done', resolved: 'Resolved', closed: 'Closed' };
      const byStatus = {};
      for (const s of order) byStatus[s] = [];
      for (const item of this.filteredItems) {
        if (item.requirement_type_name === 'Epic') continue;
        const s = item.status || 'open';
        if (byStatus[s]) byStatus[s].push(item);
      }
      return order
        .filter(s => byStatus[s].length > 0 || ['open', 'in_progress', 'done'].includes(s))
        .map(s => ({ status: s, label: labels[s] || s, items: byStatus[s] || [] }));
    },
    bulkStatusOptions() {
      // Same list the drawer's status picker uses — see SELECTABLE_STATUSES.
      return SELECTABLE_STATUSES.map((value) => ({ value, label: STATUS_LABELS[value] || value }));
    },
    collapsedTypeChips() {
      return [
        { key: 'feature_group', label: 'Feature Requests', types: ['Epic', 'Feature'] },
        { key: 'bugs_support', label: 'Bugs & Support', types: ['Bug', 'Support Ticket'] },
      ];
    },
    visibleTypeFilters() {
      // Used in the Filters panel Type picker for granular individual-type selection.
      // Customers get plain-language filters (no "Epics"/internal jargon).
      // "Feature Requests" MUST cover Epic as well as Feature: customerTypeLabel
      // renders both as "Feature Request", so to a customer they are one thing.
      // With an Epic-less chip list there was no way to select or deselect
      // Epics, and they leaked into every other chip's results.
      if (this.isCustomerView) {
        return [
          { key: 'feature', label: 'Feature Requests', types: ['Epic', 'Feature'] },
          { key: 'bug', label: 'Bug Reports', types: ['Bug'] },
          { key: 'ticket', label: 'Support Tickets', types: ['Support Ticket'] },
        ];
      }
      return this.typeFilters;
    },
    visibleColCount() {
      return this.columns.length + (this.bulkMode ? 1 : 0);
    },
    allChecked() {
      const checkable = this.pagedItems.filter((i) => !i._isGroup);
      return checkable.length > 0 && checkable.every((i) => this.checkedIds.has(i.id));
    },
    someChecked() {
      return this.pagedItems.some((i) => !i._isGroup && this.checkedIds.has(i.id));
    },
    totalPages() {
      return Math.ceil(this.sortedItems.length / this.pageSize);
    },
    pagedItems() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.sortedItems.slice(start, start + this.pageSize);
    },
    pageRange() {
      const range = [];
      const delta = 2;
      const left = Math.max(1, this.currentPage - delta);
      const right = Math.min(this.totalPages, this.currentPage + delta);
      for (let i = left; i <= right; i++) range.push(i);
      return range;
    },
  },
  watch: {
    customerPreview(v) {
      if (v) sessionStorage.setItem('service_desk_customer_preview', '1');
      else sessionStorage.removeItem('service_desk_customer_preview');
      // "Needs Input" means opposite things in each view (blocked on customer vs.
      // waiting on you). Clear it on toggle so a filter engaged in one view
      // doesn't silently carry meaning into the other.
      this.activePreset = null;
    },
    openFilterCol() {
      // requester filter is now in the panel; no cleanup needed here
    },
    detailItemId(id) {
      const current = this.selectedId != null ? Number(this.selectedId) : null;
      if (id !== current) this.$emit('select', id);
    },
    selectedId: {
      immediate: true,
      handler(id) {
        this.detailItemId = id != null && id !== '' ? Number(id) : null;
      },
    },
    filteredItems() {
      this.currentPage = 1;
    },
    filterState: {
      deep: true,
      handler(state) {
        localStorage.setItem('service_desk_v2_filters', JSON.stringify({
          ...state,
          v: FILTER_SCHEMA_VERSION,
          requirement_type_name: [...state.requirement_type_name],
          status: [...state.status],
          priority_score: [...state.priority_score],
          requester: [...state.requester],
        }));
      },
    },
    current_tenant_slug() {
      this.reloadForCacheContext();
    },
    current_tenant_name(value, previousValue) {
      // On initial login the slug can be restored before tenantAccess has
      // hydrated. Revisit the scoped cache once we know which tenant it names.
      if (!this.isStaffUser && value && !previousValue) this.reloadForCacheContext();
    },
    'current_user.id'() {
      this.reloadForCacheContext();
    },
  },
  async mounted() {
    this.restoreFilters();
    this.loadCachedItems();
    const loaders = [this.loadItems(), this.loadRequirementTypes(), this.loadUsers()];
    await Promise.allSettled(loaders);
    if (!this.sortField) this.autoExpandGroups();
    document.addEventListener('click', this.onDocClick);
    window.addEventListener('focus', this.onRevalidate);
    document.addEventListener('visibilitychange', this.onRevalidate);
    this.pollTimer = setInterval(this.onRevalidate, 60000);
    this.clockTimer = setInterval(() => { this.now = Date.now(); }, 15000);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('focus', this.onRevalidate);
    document.removeEventListener('visibilitychange', this.onRevalidate);
    clearInterval(this.pollTimer);
    clearInterval(this.clockTimer);
    clearTimeout(this.retryTimer);
  },
  methods: {
    cacheKey() {
      return cacheKeyFor(this.current_tenant_slug, this.current_user?.id);
    },
    itemsStore() {
      const key = this.cacheKey();
      return key ? new BrowserStore(key, ITEMS_CACHE_SCHEMA) : null;
    },
    reloadForCacheContext() {
      // A tenant/user switch must not leave the previous context on screen
      // while the replacement request is in flight.
      this.items = [];
      // checkedIds holds ids from the outgoing context. Left alone, the bulk
      // bar keeps showing "N selected" and a bulk action PATCHes the previous
      // tenant's requirements while the new tenant is on screen.
      this.clearBulkSelection();
      this.loading = true;
      this.fetchError = false;
      this.loadCachedItems();
      this.loadItems();
    },
    clearBulkSelection() {
      this.bulkDropdown = null;
      this.checkedIds = new Set();
      this.bulkMode = false;
    },
    // Snapshot the loaded rows behind the current selection. Ids with no row in
    // `items` are dropped: clearBulkSelection() already runs on a tenant/user
    // switch, so a stale id is unreachable today — this keeps a future
    // context-switch path from PATCHing another tenant's requirements.
    selectionSnapshot(describe) {
      return [...this.checkedIds]
        .map((id) => ({ id, item: this.items.find((i) => i.id === id) }))
        .filter((entry) => entry.item)
        .map((entry) => ({ ...entry, ...describe(entry.item) }));
    },
    onRevalidate() {
      if (document.visibilityState === 'visible') this.loadItems();
    },
    evictOtherItemCaches() {
      // Staff receive the same cross-tenant response under every
      // selected-tenant key, so retaining historical context entries
      // multiplies storage without adding value.
      evictStaleItemCaches(CACHE_KEY_PREFIX, this.cacheKey());
    },
    persistItems() {
      const store = this.itemsStore();
      if (!store) return;
      this.evictOtherItemCaches();
      store.save({ items: this.items });
    },
    restoreFilters() {
      try {
        const saved = localStorage.getItem('service_desk_v2_filters');
        if (!saved) return;
        const s = JSON.parse(saved);
        if (s.search) this.search = s.search;
        if (s.sortField) this.sortField = s.sortField;
        if (s.sortDir) this.sortDir = s.sortDir;
        const cf = { ...this.columnFilters };
        if (Array.isArray(s.requirement_type_name)) cf.requirement_type_name = new Set(s.requirement_type_name);
        // Status is the one filter whose stored values changed meaning: the
        // customer 'in_progress' bucket used to include waiting tickets and no
        // longer does. Restoring a pre-v2 set would silently hide every ticket
        // that needs the customer's input, so drop it and keep the rest.
        if (Array.isArray(s.status) && s.v === FILTER_SCHEMA_VERSION) cf.status = new Set(s.status);
        if (s.due_date_from) cf.due_date_from = s.due_date_from;
        if (s.due_date_to) cf.due_date_to = s.due_date_to;
        if (Array.isArray(s.priority_score)) cf.priority_score = new Set(s.priority_score);
        if (Array.isArray(s.requester)) cf.requester = new Set(s.requester);
        this.columnFilters = cf;
      } catch {
        // ignore
      }
    },
    filterItems(data) {
      const tenantName = this.current_tenant_name;
      // Use the effective view so a staff "Customer view" preview also hides
      // internal (customer_facing=false) rows client-side.
      const isStaff = this.effectiveStaffView;
      return data.filter(
        (item) =>
          VISIBLE_TYPES.has(item.requirement_type_name) &&
          item.status !== 'draft' &&
          // Redundant for staff: loadItems() sends tenant_scoped, so the
          // backend has already hard-scoped the response -- including for
          // superusers, who are otherwise cross-tenant. Kept for non-staff,
          // whose tenant_name is the only client-side check available.
          (this.isStaffUser || !tenantName || item.tenant_name === tenantName) &&
          (isStaff || item.customer_facing !== false),
      );
    },
    itemPriority(item) {
      if (typeof item.priority === 'number') return item.priority;
      return PRIORITY_MAP[item.priority] ?? null;
    },
    loadCachedItems() {
      // A poisoned entry fails schema validation and load() returns the []
      // default, so it's never treated as a hit — the next successful fetch
      // overwrites it via persistItems.
      // Non-staff may have a restored slug before tenantAccess has populated
      // current_tenant_name. Until it has, there is no safe client-side check
      // that a cached row belongs to the selected tenant.
      if (!this.isStaffUser && !this.current_tenant_name) return;
      const store = this.itemsStore();
      if (!store) return;
      const cached = store.load().items;
      if (!cached.length) return;
      this.items = this.filterItems(cached);
      if (this.items.length) this.loading = false;
    },
    async loadItems() {
      if (!this.loading) this.refreshing = true;
      const requestCacheKey = this.cacheKey();
      try {
        // tenant_scoped forces the backend to hard-filter to the viewed tenant.
        // It is applied after the superuser branch, so it overrides the Support
        // viewset's cross-tenant default for superusers too -- the same
        // mechanism ExternalFocusItems.vue uses.
        //
        // Unconditional, not staff-gated: the Service Desk is the per-customer
        // queue, so "which tenant am I looking at" has to mean the same thing
        // for a superuser as for anyone else. A side effect is that null-tenant
        // platform tickets drop out of this surface entirely -- they belong in
        // the internal Work list, which is deliberately cross-tenant.
        const params = { tenant_scoped: true };
        if (!this.isStaffUser) params.customer_facing = true;
        const response = await axios.get('/api/requirements/', { params });
        if (!Array.isArray(response.data)) {
          throw new Error('Malformed /api/requirements/ response: expected an array');
        }
        // The axios tenant interceptor uses the active tenant at request time.
        // Do not let a response from a prior tenant/user context overwrite the
        // current list or its cache after a switch.
        if (requestCacheKey !== this.cacheKey()) return;
        this.items = this.filterItems(response.data);
        this.persistItems();
        this.lastFetchedAt = Date.now();
        this.now = this.lastFetchedAt;
        this.fetchError = false;
        this.retryAttempt = 0;
        clearTimeout(this.retryTimer);
      } catch {
        if (requestCacheKey !== this.cacheKey()) return;
        if (!this.items.length) this.items = [];
        this.fetchError = true;
        this.scheduleRetry();
      } finally {
        if (requestCacheKey === this.cacheKey()) {
          this.loading = false;
          this.refreshing = false;
        }
      }
    },
    scheduleRetry() {
      clearTimeout(this.retryTimer);
      const delay = Math.min(30000, 2000 * 2 ** this.retryAttempt);
      this.retryAttempt++;
      this.retryTimer = setTimeout(this.onRevalidate, delay);
    },
    retryNow() {
      clearTimeout(this.retryTimer);
      this.retryAttempt = 0;
      this.loadItems();
    },
    // Additive chip selection: an empty set means "no filter, show everything"
    // and every chip reads neutral; clicking a chip narrows to it; clicking it
    // again releases it. Deselecting the last chip returns to showing
    // everything, which is what the user means by that click.
    //
    // This replaces a subtractive model where the chips all started lit and the
    // first click on one *hid* that type. Two problems: clicking "Support
    // Tickets" to see support tickets did the opposite of what it said, and the
    // click seeded the filter set with all four internal types — including
    // 'Epic', which the customer chip list has no way to clear. Epics then
    // stuck in the filter permanently and showed up under every other chip.
    toggleType(group) {
      const f = new Set(this.columnFilters.requirement_type_name);
      if (group.types.every((t) => f.has(t))) {
        group.types.forEach((t) => f.delete(t));
      } else {
        group.types.forEach((t) => f.add(t));
      }
      this.columnFilters = { ...this.columnFilters, requirement_type_name: f };
    },
    isTypeFilterActive(group) {
      const f = this.columnFilters.requirement_type_name;
      if (f.size === 0) return false;
      return group.types.every((t) => f.has(t));
    },
    sortBy(key) {
      if (this.sortField === key) {
        if (this.sortDir === 'asc') {
          this.sortDir = 'desc';
        } else {
          this.sortField = null;
          this.sortDir = 'asc';
        }
      } else {
        this.sortField = key;
        this.sortDir = 'asc';
      }
      this.currentPage = 1;
    },
    toggleExpandAll() {
      if (this.allExpanded) {
        this.expandedEpicIds = new Set();
      } else {
        this.expandedEpicIds = new Set(this.allExpandableKeys);
      }
    },
    toggleEpic(id) {
      const s = new Set(this.expandedEpicIds);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      this.expandedEpicIds = s;
    },
    setPreset(name) {
      if (name === null) {
        this.activePreset = null;
        // "All" also clears column filters and search for a full reset
        this.columnFilters = { requirement_type_name: new Set(), status: new Set(), due_date_from: '', due_date_to: '', priority_score: new Set(), requester: new Set() };
        this.search = '';
      } else {
        this.activePreset = this.activePreset === name ? null : name;
      }
    },
    openFilterPanel() {
      this.showFilterPanel = !this.showFilterPanel;
      if (this.showFilterPanel) {
        this.filterPanelAddMenu = false;
        this.expandedFilterField = null;
        // Seed rows from any already-active column filters (e.g. set via chips).
        const seeded = [];
        if (this.columnFilters.requirement_type_name.size > 0) seeded.push('type');
        if (this.columnFilters.status.size > 0) seeded.push('status');
        if (this.columnFilters.requester.size > 0) seeded.push('requester');
        if (this.columnFilters.priority_score.size > 0) seeded.push('priority');
        this.filterPanelKeys = seeded;
        this.$nextTick(() => {
          const btn = this.$refs.filterPanelBtn;
          if (btn) {
            const rect = btn.getBoundingClientRect();
            this.filterPanelStyle = {
              top: rect.bottom + 4 + 'px',
              left: Math.max(8, rect.right - 420) + 'px',
            };
          }
        });
      }
    },
    addFilterToPanel(key) {
      this.filterPanelAddMenu = false;
      if (!this.filterPanelKeys.includes(key)) {
        this.filterPanelKeys = [...this.filterPanelKeys, key];
      }
      this.expandedFilterField = key;
    },
    clearFilterFromPanel(key) {
      const keyMap = {
        type: 'requirement_type_name',
        status: 'status',
        requester: 'requester',
        priority: 'priority_score',
      };
      const filterKey = keyMap[key];
      if (filterKey) {
        this.columnFilters = { ...this.columnFilters, [filterKey]: new Set() };
      }
      this.filterPanelKeys = this.filterPanelKeys.filter((k) => k !== key);
      if (this.expandedFilterField === key) this.expandedFilterField = null;
      if (key === 'requester') this.panelRequesterSearch = '';
    },
    filterFieldLabel(key) {
      return { type: 'Type', status: 'Status', requester: 'Requester', priority: 'Priority' }[key] || key;
    },
    filterValueSummary(key) {
      const values = this.filterValueList(key);
      if (values.length === 0) return 'Select...';
      if (values.length <= 2) return values.join(', ');
      return `${values.length} selected`;
    },
    filterValueList(key) {
      if (key === 'type') {
        return [...this.columnFilters.requirement_type_name].map((t) =>
          this.isCustomerView ? customerTypeLabel(t) : t
        );
      }
      if (key === 'status') {
        return [...this.columnFilters.status].map((s) =>
          this.isCustomerView
            ? CUSTOMER_STATUS_LABELS[s] || s
            : STATUS_LABELS[s] || s
        );
      }
      if (key === 'requester') {
        return [...this.columnFilters.requester].map((id) => {
          const opt = this.requesterFilterOptions.find((o) => o.id === id);
          return opt?.name || 'Unknown';
        });
      }
      if (key === 'priority') {
        return [...this.columnFilters.priority_score].map((p) => {
          if (p === 'unset') return 'Not rated';
          const level = this.priorityLevels.find((l) => l.value === p);
          return level?.label || String(p);
        });
      }
      return [];
    },
    toggleFilterDropdown(colKey) {
      if (this.openFilterCol === colKey) {
        this.openFilterCol = null;
        return;
      }
      const refKey = 'filterBtn_' + colKey;
      const btn = this.$refs[refKey]?.[0] || this.$refs[refKey];
      if (btn) {
        const rect = btn.getBoundingClientRect();
        this.filterDropdownStyle = {
          top: rect.bottom + 4 + 'px',
          left: rect.left + 'px',
        };
      }
      this.openFilterCol = colKey;
    },
    isColumnFilterActive(key) {
      if (key === 'due_date') return !!(this.columnFilters.due_date_from || this.columnFilters.due_date_to);
      // requester_name column maps to the requester Set
      const filterKey = key === 'requester_name' ? 'requester' : key;
      const f = this.columnFilters[filterKey];
      if (f instanceof Set) return f.size > 0;
      return false;
    },
    toggleColumnFilter(field, value) {
      const s = new Set(this.columnFilters[field]);
      if (s.has(value)) s.delete(value);
      else s.add(value);
      this.columnFilters = { ...this.columnFilters, [field]: s };
    },
    clearColumnFilter(field) {
      if (field === 'due_date') {
        this.columnFilters = { ...this.columnFilters, due_date_from: '', due_date_to: '' };
      } else {
        this.columnFilters = { ...this.columnFilters, [field]: new Set() };
      }
      this.openFilterCol = null;
    },
    onDocClick() {
      if (this.openFilterCol) this.openFilterCol = null;
      if (this.bulkDropdown) this.bulkDropdown = null;
      if (this.showFilterPanel) { this.showFilterPanel = false; this.filterPanelAddMenu = false; }
    },
    // Clears everything that narrows the list, not just the column filters --
    // search and the triage filter hide rows too, and a control labelled "Clear
    // filters" that leaves them applied is a broken promise.
    resetAll() {
      this.sortField = null;
      this.sortDir = 'asc';
      this.activePreset = null;
      this.columnFilters = { requirement_type_name: new Set(), status: new Set(), due_date_from: '', due_date_to: '', priority_score: new Set(), requester: new Set() };
      this.openFilterCol = null;
      this.filterPanelKeys = [];
      this.expandedFilterField = null;
      this.search = '';
      this.currentPage = 1;
    },
    isDone(item) {
      return DONE_STATUSES.has(item.status);
    },
    effectiveDate(item) {
      if (DONE_STATUSES.has(item.status)) return item.completed_at || item.updated_at || '';
      return item.due_date || '';
    },
    autoExpandGroups() {
      const keys = this.allExpandableKeys.filter((k) => k !== '__completed__');
      if (keys.length > 0) this.expandedEpicIds = new Set(keys);
    },
    relativeTime(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffDays = Math.floor((now - date) / 86400000);
      if (diffDays === 0) return 'today';
      if (diffDays === 1) return 'yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
      }
      return this.formatDate(dateStr);
    },
    setPriority(item, value) {
      const oldPriority = item.priority;
      item.priority = value;
      this.persistItems();
      const label = value != null ? `P${value}` : 'None';
      this.$refs.undoToast.show(
        `Priority set to ${label}`,
        () => axios.patch(`/api/requirements/${item.id}/`, { priority: value ?? 'none' }),
        () => { item.priority = oldPriority; this.persistItems(); },
      );
    },
    formatDate(date) {
      return formatDateValue(date);
    },
    relativeDueDate(date) {
      const diffDays = daysUntil(date);
      if (diffDays == null) return '';
      if (diffDays < -30) {
        const weeks = Math.abs(Math.round(diffDays / 7));
        return `Overdue by ${weeks} week${weeks === 1 ? '' : 's'}`;
      }
      if (diffDays < -1) return `Overdue by ${Math.abs(diffDays)} days`;
      if (diffDays === -1) return 'Overdue by 1 day';
      if (diffDays === 0) return 'Due today';
      if (diffDays === 1) return 'Due tomorrow';
      if (diffDays <= 7) return `Due in ${diffDays} days`;
      if (diffDays <= 30) {
        const weeks = Math.round(diffDays / 7);
        return `Due in ${weeks} week${weeks === 1 ? '' : 's'}`;
      }
      return this.formatDate(date);
    },
    dueDateClass(date) {
      const diffDays = daysUntil(date);
      if (diffDays == null) return 'text-gray-300';
      if (diffDays < 0) return 'text-red-400';
      if (diffDays <= 3) return 'text-amber-500';
      if (diffDays <= 7) return 'text-amber-400';
      return 'text-gray-500';
    },
    isDueSoon(date) {
      const diffDays = daysUntil(date);
      return diffDays != null && diffDays >= 0 && diffDays <= 3;
    },
    dueDateLabel(date) {
      const diffDays = daysUntil(date);
      if (diffDays == null) return '';
      if (diffDays < -1) return `${Math.abs(diffDays)}d overdue`;
      if (diffDays === -1) return 'Yesterday';
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Tomorrow';
      if (diffDays <= 7) return `${diffDays}d`;
      return this.formatDate(date);
    },
    initials(name) {
      if (!name) return '';
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0][0].toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    highlightSearch(text) {
      if (!this.search || !text) return this.escapeHtml(text || '');
      const escaped = this.escapeHtml(text);
      const query = this.search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(${query})`, 'gi');
      return escaped.replace(re, '<mark class="bg-amber-200/60 text-inherit rounded-sm px-0.5">$1</mark>');
    },
    // When the search match is in description/notes (not title), return a short
    // excerpt so the user knows WHY this row appeared. Returns null when the
    // title already contains the match (no excerpt needed) or when not searching.
    searchMatchExcerpt(item) {
      if (!this.search || this.isIdSearch) return null;
      const q = this.search.toLowerCase().trim();
      if (!q) return null;
      // If the title matches, no excerpt needed — the highlighted title is enough.
      if (item.title?.toLowerCase().includes(q)) return null;
      if (String(item.id).includes(q)) return null;
      if (item.description?.toLowerCase().includes(q)) {
        return this.excerptAround(item.description, q);
      }
      if (item.requester_name?.toLowerCase().includes(q)) {
        return `Requester: ${item.requester_name}`;
      }
      // internal_notes are staff-only — never render an excerpt in customer view.
      if (this.effectiveStaffView && item.internal_notes?.toLowerCase().includes(q)) {
        return this.excerptAround(item.internal_notes, q);
      }
      return null;
    },
    excerptAround(text, query, radius = 45) {
      const idx = text.toLowerCase().indexOf(query);
      if (idx < 0) return '';
      const start = Math.max(0, idx - radius);
      const end = Math.min(text.length, idx + query.length + radius);
      return (start > 0 ? '…' : '') + text.slice(start, end).trim() + (end < text.length ? '…' : '');
    },
    escapeHtml(str) {
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },
    statusDotClass(status) {
      const map = {
        approved: 'bg-blue-400',
        in_progress: 'bg-amber-400',
        open: 'bg-red-400',
        waiting: 'bg-orange-400',
        done: 'bg-green-400',
        resolved: 'bg-green-400',
        closed: 'bg-gray-300',
        // customer buckets
        received: 'bg-blue-400',
      };
      return map[status] || 'bg-gray-300';
    },
    statusBorderClass(status) {
      const map = {
        approved: 'border-l-blue-400',
        in_progress: 'border-l-amber-400',
        open: 'border-l-red-400',
        waiting: 'border-l-orange-400',
        done: 'border-l-green-400',
        resolved: 'border-l-green-400',
        closed: 'border-l-gray-300',
      };
      return map[status] || 'border-l-gray-200';
    },
    avatarColor(name) {
      const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash) + name.charCodeAt(i);
      return colors[Math.abs(hash) % colors.length];
    },
    toggleBulkMode() {
      this.bulkMode = !this.bulkMode;
      if (!this.bulkMode) this.checkedIds = new Set();
    },
    toggleCheck(id) {
      const s = new Set(this.checkedIds);
      if (s.has(id)) s.delete(id); else s.add(id);
      this.checkedIds = s;
    },
    toggleAllChecked() {
      const checkable = this.pagedItems.filter((i) => !i._isGroup);
      if (this.allChecked) {
        this.checkedIds = new Set();
      } else {
        this.checkedIds = new Set(checkable.map((i) => i.id));
      }
    },
    bulkSetPriority(value) {
      const apiValue = value ?? 'none';
      const snapshot = this.selectionSnapshot((item) => ({ old: item.priority }));
      if (!snapshot.length) return this.clearBulkSelection();
      for (const s of snapshot) { s.item.priority = value; }
      this.persistItems();
      const count = snapshot.length;
      const label = value != null ? `P${value}` : 'None';
      this.$refs.undoToast.show(
        `${count} item${count > 1 ? 's' : ''} set to ${label}`,
        () => Promise.all(snapshot.map((s) => axios.patch(`/api/requirements/${s.id}/`, { priority: apiValue }))),
        () => { for (const s of snapshot) { s.item.priority = s.old; } this.persistItems(); },
      );
      this.clearBulkSelection();
    },
    bulkSetStatus(status) {
      const snapshot = this.selectionSnapshot((item) => ({ old: item.status }));
      if (!snapshot.length) return this.clearBulkSelection();
      for (const s of snapshot) { s.item.status = status; }
      this.persistItems();
      const count = snapshot.length;
      const label = STATUS_LABELS[status] || status;
      this.$refs.undoToast.show(
        `${count} item${count > 1 ? 's' : ''} marked ${label}`,
        () => Promise.all(snapshot.map((s) => axios.patch(`/api/requirements/${s.id}/`, { status }))),
        () => { for (const s of snapshot) { s.item.status = s.old; } this.persistItems(); },
      );
      this.clearBulkSelection();
    },
    bulkSetOwner(ownerId) {
      const snapshot = this.selectionSnapshot((item) => ({ oldOwner: item.owner, oldName: item.owner_name }));
      if (!snapshot.length) return this.clearBulkSelection();
      const user = this.allUsers.find((u) => u.id === ownerId);
      const newName = user ? (user.full_name || user.email) : null;
      for (const s of snapshot) {
        s.item.owner = ownerId;
        s.item.owner_name = newName;
      }
      this.persistItems();
      const count = snapshot.length;
      this.$refs.undoToast.show(
        `${count} item${count > 1 ? 's' : ''} assigned to ${newName || 'Unassigned'}`,
        () => Promise.all(snapshot.map((s) => axios.patch(`/api/requirements/${s.id}/`, { owner: ownerId }))),
        () => { for (const s of snapshot) { s.item.owner = s.oldOwner; s.item.owner_name = s.oldName; } this.persistItems(); },
      );
      this.clearBulkSelection();
    },
    bulkSetDueDate(date) {
      const snapshot = this.selectionSnapshot((item) => ({ old: item.due_date }));
      if (!snapshot.length) return this.clearBulkSelection();
      for (const s of snapshot) { s.item.due_date = date; }
      this.persistItems();
      const count = snapshot.length;
      const label = date ? this.formatDate(date) : 'cleared';
      this.$refs.undoToast.show(
        `Due date ${date ? 'set to ' + label : label} for ${count} item${count > 1 ? 's' : ''}`,
        () => Promise.all(snapshot.map((s) => axios.patch(`/api/requirements/${s.id}/`, { due_date: date }))),
        () => { for (const s of snapshot) { s.item.due_date = s.old; } this.persistItems(); },
      );
      this.clearBulkSelection();
    },
    copyTicketId(id) {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(String(id)).catch(() => {});
      }
      this.copiedId = id;
      setTimeout(() => {
        if (this.copiedId === id) this.copiedId = null;
      }, 1200);
    },
    exportCsv() {
      const rows = [['Type', 'Title', 'Status', 'Requester', 'Due Date', 'Completed Date', 'Priority']];
      for (const item of this.filteredItems) {
        const p = this.itemPriority(item);
        rows.push([
          item.requirement_type_name,
          `"${(item.title || '').replace(/"/g, '""')}"`,
          this.statusLabel(item.status),
          item.requester_name || '',
          item.due_date || '',
          this.isDone(item) ? (item.completed_at || item.updated_at || '').slice(0, 10) : '',
          p != null ? `P${p}` : '',
        ]);
      }
      const csv = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `service-desk-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onKeydown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const items = this.pagedItems.filter((i) => !i._isGroup);
      if (!items.length) return;
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        this.focusedIndex = Math.min(this.focusedIndex + 1, items.length - 1);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
      } else if (e.key === 'Enter' && this.focusedIndex >= 0) {
        e.preventDefault();
        this.detailItemId = items[this.focusedIndex].id;
      } else if (e.key === 'Escape') {
        if (this.detailItemId) {
          this.detailItemId = null;
        } else if (this.bulkMode) {
          this.bulkMode = false;
          this.checkedIds = new Set();
        }
      } else if (e.key === 'x' && this.bulkMode && this.focusedIndex >= 0) {
        this.toggleCheck(items[this.focusedIndex].id);
      }
    },
    // "6d" for a ticket blocked on the customer, '' otherwise. waiting_since is
    // optional (parallel newCoBackend PR; never backfilled for tickets already
    // in Waiting), so the chip is simply omitted when it's absent.
    staleWaitingLabel(item) {
      if (!this.effectiveStaffView) return '';
      if (item.status !== 'waiting' || !item.waiting_since) return '';
      return daysAgoShortLabel(item.waiting_since);
    },
    statusClass(status) {
      if (this.isCustomerView) {
        return CUSTOMER_STATUS_CLASSES[customerBucket(status)] || 'bg-gray-100 text-gray-500';
      }
      return STATUS_CLASSES[status] || 'bg-gray-100 text-gray-500';
    },
    statusLabel(status) {
      if (this.isCustomerView) {
        return CUSTOMER_STATUS_LABELS[customerBucket(status)] || status;
      }
      return STATUS_LABELS[status] || status;
    },
    // Plain-language gloss on the customer chips. "Needs Input" is a new word
    // on this screen and the colour alone doesn't teach anyone what it means —
    // this is also the accessible name, so the meaning isn't carried by colour.
    statusTitle(status) {
      if (!this.isCustomerView) return STATUS_LABELS[status] || status;
      return CUSTOMER_STATUS_HINTS[customerBucket(status)] || '';
    },
    // Accessible name for a status chip. Only the customer chips get the gloss
    // appended — in the staff view statusTitle() is the label itself, so
    // concatenating would read the status twice ("Waiting. Waiting").
    statusAriaLabel(status) {
      const label = this.statusLabel(status);
      const hint = this.statusTitle(status);
      return !this.isCustomerView || !hint || hint === label ? label : `${label}. ${hint}`;
    },
    typeClass(type) {
      return TYPE_CLASSES[type] || 'bg-gray-100 text-gray-600';
    },
    typeLabel(type) {
      if (this.isCustomerView) return customerTypeLabel(type);
      return type;
    },
    async loadRequirementTypes() {
      try {
        const response = await axios.get('/api/requirement-types/');
        if (!Array.isArray(response.data)) return;
        this.allRequirementTypes = response.data;
        const commentType = response.data.find((t) => t.name === 'Comment');
        if (commentType) this.commentTypeId = commentType.id;
      } catch {
        // ignore
      }
    },
    async loadUsers() {
      try {
        const response = await axios.get('/api/user/', { params: { include_staff: 'true' } });
        if (!Array.isArray(response.data)) return;
        this.allUsers = response.data.filter((u) => u.email?.endsWith('@kaironhealth.com'));
      } catch {
        // ignore
      }
    },
    openCreate(typeName) {
      this.createDefaultType = typeName;
      this.showCreateModal = true;
    },
    async onItemCreated() {
      this.showCreateModal = false;
      await this.loadItems();
    },
    onRowClick(item) {
      if (this.bulkMode) return;
      this.detailItemId = item.id;
    },
    onTrayCommentCount({ id, count }) {
      this.commentCounts = { ...this.commentCounts, [id]: count };
    },
    // The drawer already persisted the change; just mirror it onto the row so
    // the list reflects it without a reload.
    onTrayUpdated({ id, changes }) {
      const item = this.items.find((i) => i.id === id);
      if (!item) return;
      Object.assign(item, changes);
      this.persistItems();
    },
    onTicketTriaged({ id }) {
      const item = this.items.find((i) => i.id === id);
      if (item) item.needs_triage = false;
    },
  },
};
</script>
